<template>
  <div class="w-full p-5 border-box">
    <div class="mb-2">
      <Text size="lg">
        Enter your unique API key from the REDCap project (under API tab) you'd
        like to sync.
      </Text>
    </div>
    <div class="flex w-full">
      <div class="w-half mr-8">
        <InputText
          v-model="key"
          :disabled="projectPendingSync !== false"
          :error="errorMessage !== ''"
          stretch
        />
      <Text :class="{ hidden: !error }">{{ errorMessage }}</Text>
      </div>
      <Button
        @click="lookupProject"
        :disabled="buttonDisabled"
        theme="secondary"
        size="md"
      >
        Submit
      </Button>
    </div>
    <div v-if="projectPendingSync !== false" class="mt-8 w-half">
      <Text size="sm" bold>Project Found</Text>
      <div class="card p-2 mt-4 flex justify-between">
        <div>
          <Text size="xl">{{ projectPendingSync.project_title }}</Text>
          <Text class="mt-2" size="sm">{{ createdAtDescription }}</Text>
        </div>
        <Button
          :disabled="syncingProject"
          @click="syncProject"
          theme="secondary"
          size="sm"
          >Confirm Sync</Button
        >
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, inject } from "vue"
import dayjs from "dayjs"

import ApiClient, { ProjectApi, RedcapApiTokenApi } from "../api"

import Text from "../components/Text.vue"
import InputText from "./InputText.vue"
import Button from "./Button.vue"

const props = defineProps({
  redcapApiTokens: {
    type: Array,
    required: true
  },
  projects: {
    type: Array,
    required: true
  }
})

const user = inject("user")
const errorMessage = ref("")
const error = computed(() => errorMessage.value !== "")
const syncingProject = ref(false)
const key = ref(null)
const emit = defineEmits(["project-added"])
const projectPendingSync = ref(false)

const buttonDisabled = computed(() => {
  return !key.value || !!projectPendingSync.value
})

const createdAtDescription = computed(() => {
  if (!projectPendingSync.value) return ""
  return `Created on ${dayjs(projectPendingSync.value.creation_time).format(
    "MMM D, YYYY"
  )}`
})

const tokenAlreadySynced = computed(() => {
  const tokens = props.redcapApiTokens.map((t) => t.token)
  return tokens.includes(key.value)
})

async function lookupProject() {
  if (!tokenAlreadySynced.value) {
    try {
      const response = await ApiClient.post(`/export`, {
        redcapApiToken: key.value,
        content: "projectinfo",
      })
      projectPendingSync.value = response.data
    } catch (error) {
      errorMessage.value = "No project found for this user & token combination."
    }
  } else {
    errorMessage.value = "Token already synced for this user."
  }
}

async function syncProject() {
  syncingProject.value = true
  errorMessage.value = ""
  try {
    const projectAlreadySaved = (await ProjectApi.all({
      where: { name: projectPendingSync.value.project_title }
    })).data

    let response
    if (projectAlreadySaved.length > 0) {
      response = await RedcapApiTokenApi.save({
        token: key.value,
        userId: user.value.id,
        projectId: projectAlreadySaved[0].id
      })
    } else {
      response = await ProjectApi.save(
        {
          // check if project already exists in db. if so, only add to api token and then projectId = existing project
          name: projectPendingSync.value.project_title,
          redcapApiTokens: [
            {
              token: key.value,
              userId: user.value.id
            }
          ]
          },{
            upsert: true
          }
      )
    }
    if (response.status === 200) {
      emit("project-added")
    }
  } catch (error) {
    errorMessage.value =
      "Error syncing project from REDCap. Please try again later."
  }
  syncingProject.value = false
  projectPendingSync.value = false
}
</script>
